import { useEffect, useState } from 'react'

import { Badge, Stack, Table } from 'react-bootstrap'

import { getOutgoingTicketTransfers } from '../../../../utilities/api'
import {
  capitalizeString,
  formatDateTime,
  formatPhoneNumber,
  formatSectionRowSeat,
  getTimezoneDate,
  timezones,
} from '../../../../utilities/helpers'
import { Spinner } from '../../../LoadingContainer/Spinner'

const STATUS_LABEL = {
  claimed: 'Transferred',
  pending: 'Pending',
  canceled: 'Canceled',
}

const STATUS_BADGE = {
  claimed: 'complete',
  pending: 'pending',
  canceled: 'cancelled',
}

function displayDate(value, timezone) {
  if (!value) return '—'
  const zone = String(timezone || '').includes('/')
    ? timezone
    : timezones(timezone)?.utc?.[0] || timezone
  return formatDateTime(getTimezoneDate(value, zone)) || '—'
}

function seatLabel(ticket) {
  if (ticket?.sectionNumber) return formatSectionRowSeat(ticket)
  if (ticket?.generalAdmission) return 'General admission'
  if (ticket?.name) return ticket.name
  return 'Ticket'
}

function recipientName(recipient) {
  const name = capitalizeString(recipient?.name || '')
  if (name) return name
  return recipient?.email || 'Unknown recipient'
}

export default function TransferredTicketsTab({
  email,
  userId,
  organizationUUID,
  active = true,
}) {
  const [transfers, setTransfers] = useState([])
  const [error, setError] = useState('')
  const [loadedKey, setLoadedKey] = useState('')
  const requestKey = [email || '', userId || '', organizationUUID || ''].join(
    '|'
  )
  const canLoad = Boolean(active && organizationUUID && (email || userId))
  const loading = canLoad && loadedKey !== requestKey && !error

  useEffect(() => {
    if (!canLoad) return undefined

    let cancelled = false
    setError('')

    getOutgoingTicketTransfers({ email, userId, organizationUUID })
      .then((response) => {
        if (cancelled) return
        const next = response?.data?.transfers
        setTransfers(Array.isArray(next) ? next : [])
        setLoadedKey(requestKey)
      })
      .catch((requestError) => {
        if (cancelled) return
        setTransfers([])
        setError(
          requestError?.response?.data?.message ||
            'Could not load transferred tickets.'
        )
      })

    return () => {
      cancelled = true
    }
  }, [canLoad, email, userId, organizationUUID, requestKey])

  if (!email && !userId) {
    return (
      <div className="text-center py-5 text-muted">
        This account has no email or user id, so transferred tickets cannot be
        looked up.
      </div>
    )
  }

  if (!organizationUUID) {
    return (
      <div className="text-center py-5 text-muted">
        An organization is required to look up transferred tickets.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5 gap-2">
        <Spinner variant="dark" size="sm" />
        <span className="text-muted">Loading transferred tickets...</span>
      </div>
    )
  }

  if (error) {
    return <div className="text-center py-5 text-danger">{error}</div>
  }

  return (
    <div>
      <p className="small text-muted mb-3">
        Tickets this account sent to someone else.
      </p>
      <div className="table-container">
        <div className="table-container--fixed-height--scrollable">
          <Table responsive>
            <thead>
              <tr className="with-inside-borders">
                <th className="text-left">Event</th>
                <th className="text-left">Location</th>
                <th className="text-left">Transferred to</th>
                <th className="text-left">Date</th>
                <th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {transfers.length === 0 ? (
                <tr className="with-inside-borders">
                  <td colSpan={5} className="text-center text-muted py-4">
                    No tickets have been transferred from this account.
                  </td>
                </tr>
              ) : (
                transfers.map((transfer) => {
                  const status = transfer.status || 'pending'
                  const seats = transfer.tickets || []
                  const when = transfer.transferredOn || transfer.createdAt
                  const phone = transfer.recipient?.phoneNumber
                  const emailAddress = transfer.recipient?.email
                  const name = recipientName(transfer.recipient)

                  return (
                    <tr key={transfer.id} className="with-inside-borders">
                      <td className="text-left">
                        <div>{transfer.event?.name || 'Event'}</div>
                        {transfer.event?.start && (
                          <div className="small text-muted">
                            {displayDate(
                              transfer.event.start,
                              transfer.event.timezone
                            )}
                          </div>
                        )}
                        {transfer.transferType === 'access_pass' && (
                          <div className="small text-muted">Access pass</div>
                        )}
                      </td>
                      <td className="text-left">
                        {seats.length === 0 ? (
                          'Ticket details unavailable'
                        ) : (
                          <Stack gap={1}>
                            {seats.map((ticket, index) => (
                              <span key={ticket.id || index}>
                                {seatLabel(ticket)}
                              </span>
                            ))}
                          </Stack>
                        )}
                      </td>
                      <td className="text-left">
                        <div>{name}</div>
                        {emailAddress && name !== emailAddress && (
                          <div className="small text-muted">{emailAddress}</div>
                        )}
                        {phone && (
                          <div className="small text-muted">
                            {formatPhoneNumber(phone)}
                          </div>
                        )}
                      </td>
                      <td className="text-left">
                        <div>{displayDate(when, transfer.event?.timezone)}</div>
                        {transfer.orderId && (
                          <div className="small text-muted">
                            Order {transfer.orderId}
                          </div>
                        )}
                        {transfer.sentByEmail &&
                          transfer.sentByEmail.toLowerCase() !==
                            String(email || '')
                              .trim()
                              .toLowerCase() && (
                            <div className="small text-muted">
                              Sent by {transfer.sentByEmail}
                            </div>
                          )}
                      </td>
                      <td>
                        <Badge
                          bg="none"
                          className={`badge-sm badge--${
                            STATUS_BADGE[status] || 'muted'
                          } w-max-content`}
                        >
                          {STATUS_LABEL[status] || 'Pending'}
                        </Badge>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  )
}
